import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  NgZone,
  OnChanges
} from '@angular/core';
import {
  ChartType,
  DEFAULT_CHART_FONT,
  DEFAULT_CHART_FONT_SIZE,
  DEFAULT_LINE_CHART_COLORS,
  DEFAULT_LINE_CHART_PREFIXES_BIGNUM
} from '../charts.constants';
import { ChartData } from '../models/ChartData';

declare let AmCharts: any;

@Component({
  selector: 'app-line-chart',
  template:
    '<div #lineChartCont style="width: 100%;" [style.height]="height"></div>',
  styleUrls: ['./line-chart.component.scss']
})
export class LineChartComponent implements OnChanges {
  @Input() data: Array<ChartData>;
  @Input() height = '280px';
  @Input() type: ChartType;
  @Input() titleArray: Array<string>;
  @Input() valueTextPrefix = '';
  @Input() valueAxisUnit = '';
  @Input() usePrefixes = true;
  @Input() hideLegend = false;
  @Input() minPeriod = 'MM';
  @Input() dataDateFormat = 'YYYY-MM';
  @Input() categoryBalloonDateFormat = 'YYYY-MM';
  @Input() categoryField: string;
  @Input() valueFields: string[];
  @Input() valueAxisFracDigits = 2;
  @Input() valueAxisPrecision = 0;
  @Input() lineThickness = 2;
  @Input() colors = DEFAULT_LINE_CHART_COLORS;
  @Input() graphFillColors = '';
  @Input() graphLineColor = '';

  @ViewChild('lineChartCont', { static: true }) lineChartCont: ElementRef;

  constructor(private _ngZone: NgZone) {}

  ngOnChanges() {
    this._ngZone.runOutsideAngular(() => {
      var lineChart = new AmCharts.AmSerialChart(AmCharts.themes['light']);
      lineChart.dataProvider = this.data;
      lineChart.categoryField = this.categoryField;
      lineChart.dataDateFormat = this.dataDateFormat;
      lineChart.fontFamily = DEFAULT_CHART_FONT;
      lineChart.fontSize = DEFAULT_CHART_FONT_SIZE;
      lineChart.colors = this.colors;
      lineChart.maxZoomFactor = 1;

      if (this.usePrefixes) {
        lineChart.usePrefixes = true;
        lineChart.prefixesOfBigNumbers = DEFAULT_LINE_CHART_PREFIXES_BIGNUM;
        lineChart.prefixesOfSmallNumbers = [];
        if (this.valueAxisUnit !== '%') {
          lineChart.precision = this.valueAxisPrecision;
        }
      }

      var categoryAxis = lineChart.categoryAxis;
      categoryAxis.minPeriod = this.minPeriod;
      categoryAxis.dashLength = 3;
      categoryAxis.minorGridEnabled = true;
      categoryAxis.minorGridAlpha = 0.1;
      categoryAxis.markPeriodChange = false;
      categoryAxis.gridAlpha = 0;
      categoryAxis.parseDates = true;
      categoryAxis.labelFrequency = 1;
      categoryAxis.minHorizontalGap = this.minPeriod === 'hh' ? 75 : 15;

      var valueAxis = new AmCharts.ValueAxis();
      valueAxis.axisAlpha = 0;
      if (this.valueAxisUnit) {
        valueAxis.unit = this.valueAxisUnit;
      }

      if (this.valueTextPrefix) {
        valueAxis.labelFunction = (value, valueText, axis) => {
          return `${this.valueTextPrefix}${valueText}`;
        };
      }

      if (this.valueAxisUnit === '%') {
        valueAxis.labelFunction = (value, valueText, axis) => {
          return `${parseFloat((value * 100).toString()).toFixed(
            this.valueAxisFracDigits
          )}%`;
        };
      }

      lineChart.addValueAxis(valueAxis);
      lineChart.addGraph(
        this.makeGraph(this.valueFields[0], this.titleArray[0])
      );

      if (this.type === 'double' && this.valueFields[1]) {
        lineChart.addGraph(
          this.makeGraph(this.valueFields[1], this.titleArray[1])
        );
      }

      if (!this.hideLegend) {
        var legend = new AmCharts.AmLegend();
        legend.position = 'top';
        legend.borderAlpha = 0.3;
        legend.horizontalGap = 10;
        legend.fontSize = 14;
        legend.switchType = 'v';
        legend.spacing = 120;
        if (this.valueAxisUnit === '%') {
          legend.valueFunction = (value, valueText, axis) => {
            return `${this.valueTextPrefix}${this.parseValue(valueText)} ${
              this.valueAxisUnit
            }`;
          };
        } else {
          legend.valueText = `${this.valueTextPrefix}[[value]]`;
        }
        legend.valueWidth = 100;
        legend.valueAlign = 'right';
        legend.switchable = false;
        lineChart.addLegend(legend);
      } else {
        lineChart.removeLegend();
      }

      var chartCursor = new AmCharts.ChartCursor();
      chartCursor.cursorAlpha = 0.5;
      chartCursor.cursorPosition = 'mouse';
      chartCursor.categoryBalloonDateFormat = this.categoryBalloonDateFormat;
      lineChart.addChartCursor(chartCursor);
      lineChart.creditsPosition = 'top-right';
      lineChart.write(this.lineChartCont.nativeElement);

      this._ngZone.run(() => {
        console.debug('LINE Chart render done!');
      });
    });
  }

  makeGraph(valueField: string, valueTitle: string) {
    var graph = new AmCharts.AmGraph();
    graph.type = 'smoothedLine';
    graph.valueField = valueField;
    graph.title = valueTitle;

    if (this.graphFillColors || this.graphLineColor) {
      graph.fillAlphas = 1;
      graph.fillColors = this.graphFillColors;
      graph.lineColor = this.graphLineColor;
    }

    graph.bullet = 'round';
    graph.bulletSize = this.lineThickness + 4;
    graph.lineThickness = this.lineThickness;

    if (this.valueAxisUnit === '%') {
      graph.balloonFunction = (value, valueText, axis) => {
        return `<b>${valueTitle}</b><br><span style='font-size:12px'>${
          value.dataContext[this.categoryField]
        }:
        <b>${this.valueTextPrefix}${this.parseValue(
          value.dataContext[valueField]
        )}
        ${this.valueAxisUnit}</b></span>`;
      };
    } else {
      graph.balloonText = `<b>[[title]]</b><br><span style="font-size:12px">[[category]]: <b>${this.valueTextPrefix}[[value]]${this.valueAxisUnit}</b></span>`;
    }

    return graph;
  }

  private parseValue(value: any): any {
    return parseFloat((value * 100).toString()).toFixed(
      this.valueAxisFracDigits
    );
  }
}
